// The deploy ask (R1883, `docs/design/ui-map-interface.md` §9 "What a
// control group does").
//
// The digit of a control group whose fleet sits in the pens, and a short
// press on its slot, select the fleet as they select a fleet at sea. A
// hull in the pens is not on the map, so its card is the deploy ask: one
// card of the card family in the selection's place of the context slot,
// with one sentence and its presses. It lives as every fleet card lives
// (R1988): another selection and a deselect take it away. This file is
// the pure half: which state the ask is in and what the card says. The
// overlay chooses the card for the selection and owns the presses.
//
// **A fleet under repair does not sail.** A repair job in the queue names
// the hull it mends (`GameState.underRepair`), and the hull keeps its
// wounds until the job ends. The
// card then offers no DEPLOY and states an error, and every deploy path of
// the client refuses the hull the same way (`Overlay.refuseDeploy`). The
// server refuses it too; the client check is a courtesy that answers
// before the round trip.

import type { ShipStock } from '../gen/proto/aquaphobia';
import { KEYS } from '../gen/presentation';
import type { GameState } from '../state';
import { card as CS, overlay as S } from '../gen/strings';
import { groupChipHtml, type Action, type CardSpec, type Fact } from './cards/card';
import { esc } from './esc';
import { hullBand, pctFig } from './format';

/**
 * The step that blocks the deploy of a hull in the pens, which is the one
 * press of its row (R1888, mockup round 091 card 02). The server refuses
 * the deploy of a hull while a repair of it stands in the queue (R1880)
 * and the deploy of a warship with nobody aboard (R385). Wear alone never
 * blocks a deploy, so REPAIR is never the press: it stands in ⋯, and the
 * HULL bar states the wear.
 *
 * - `repairing`: a repair of the hull is in the queue; the press is the
 *   dim word UNDER REPAIR, and it acts on nothing.
 * - `captain`: a warship with no officer; the press is CAPTAIN, which
 *   opens the captain choice under the row.
 * - `deploy`: nothing blocks; the press is DEPLOY.
 */
export type PrepStep = 'repairing' | 'captain' | 'deploy';

export function prepStep(o: { support: boolean; captain: boolean; repairing: boolean }): PrepStep {
  if (o.repairing) return 'repairing';
  if (!o.support && !o.captain) return 'captain';
  return 'deploy';
}

/**
 * The step of one hull in the pens, by the one captain test of the client
 * (R3017): a warship has an officer when its `captainId` names a captain
 * of the empire frame. The pens row of FLEET · MISSIONS and the deploy ask
 * both read it, so the two never disagree for one frame.
 */
export function penStep(s: Pick<GameState, 'ship' | 'captain' | 'underRepair'>, st: ShipStock): PrepStep {
  return prepStep({
    support: s.ship(st.cls)?.support ?? false,
    captain: s.captain(st.captainId) !== undefined,
    repairing: s.underRepair(st.fleetId),
  });
}

/**
 * The four states of the ask, in the order the card tests them:
 * `repairing` (no DEPLOY, an error), `nocaptain` (a warship with nobody
 * aboard, no DEPLOY, ASSIGN CAPTAIN), `damaged` (DEPLOY beside REPAIR, a
 * warning) and `ready` (DEPLOY alone).
 */
export type DeployAskState = 'ready' | 'damaged' | 'repairing' | 'nocaptain';

/** The state of the ask for one penned hull: the step of its row
 *  (`penStep`), and the wear where nothing blocks the deploy. */
export function deployAskState(st: ShipStock, step: PrepStep): DeployAskState {
  if (step === 'repairing') return 'repairing';
  if (step === 'captain') return 'nocaptain';
  return st.wearPct > 0 ? 'damaged' : 'ready';
}

/** What the card needs beside the hull. */
export interface DeployCardIn {
  st: ShipStock;
  state: DeployAskState;
  /** The reader's faction: the owner ink of the head. */
  faction: number;
  /** The 20 px hull mark of the head. */
  mark: string;
  /** The class name: the kind line under the title. */
  kind: string;
  /** A Wharf stands, so a repair can be queued (the pens rule). */
  canRepair: boolean;
  /** The control group slot of the fleet (1 to 6), 0 for none: the group chip after the name. */
  group: number;
  /** The CAPTAIN fact as plain text, the words of the card at sea; null
   *  for a support hull, which carries no officer (R385). */
  captain: string | null;
  /** The fit strip markup of `fitStrip` (R1501), '' for a basic hull. */
  systems: string;
}

/**
 * The card of the ask. The hull vital states the share left; a damaged
 * hull takes a warning line and REPAIR as its dark press, with DEPLOY
 * beside it, because the owner asked the card to suggest the repair. A
 * hull under repair takes the error line and CANCEL alone.
 */
export function deployCardSpec(i: DeployCardIn): CardSpec {
  const { st } = i;
  const left = 100 - st.wearPct;
  const fleet = st.name;
  // The two presses carry the key caps of `[keys.card]` (R1989); a
  // desktop shows them and a finger does not (`CardEnv.keycaps`).
  const go: Action = {
    act: 'deploygo', id: st.fleetId, label: S.btn_deploy, face: 'dark',
    key: KEYS.card.deploy.label.toUpperCase(),
  };
  const fix: Action = {
    act: 'deployfix', id: st.fleetId, label: S.btn_repair, face: 'dark', off: !i.canRepair,
    key: KEYS.card.repair.label.toUpperCase(),
  };
  let lines: string[];
  let actions: Action[];
  let tone: 'bad' | undefined;
  let status: CardSpec['status'];
  switch (i.state) {
    case 'repairing':
      lines = [esc(S.deploy_ask_repairing({ fleet }))];
      actions = [];
      tone = 'bad';
      status = { text: S.deploy_status_repairing, tone: 'bad' };
      break;
    case 'nocaptain':
      lines = [esc(S.deploy_ask_no_captain({ fleet }))];
      actions = [{ act: 'assigncap', id: st.fleetId, label: S.fl_assign_captain, face: 'dark' }];
      tone = 'bad';
      break;
    case 'damaged':
      lines = [esc(S.deploy_ask({ fleet })), esc(S.deploy_ask_damaged({ pct: left }))];
      actions = [fix, { ...go, face: 'quiet' }];
      status = { text: S.deploy_status_damaged, tone: 'amber' };
      break;
    default:
      lines = [esc(S.deploy_ask({ fleet }))];
      actions = [go];
  }
  // The CAPTAIN and SYSTEMS facts of the card at sea (`ownFleetCard`).
  const facts: Fact[] = [];
  if (i.captain !== null) facts.push({ key: CS.f_captain, val: esc(i.captain) });
  if (i.systems) facts.push({ key: CS.f_systems, val: i.systems });
  return {
    size: 'card', mark: i.mark, title: fleet,
    titleHtml: i.group ? groupChipHtml(i.group, i.faction) : '', close: true, ink: { faction: i.faction }, kind: i.kind, status,
    vitals: [{ label: CS.v_hull, fig: pctFig(left), frac: left / 100, ink: 'hull', low: hullBand(left) === 'bad' }],
    facts,
    note: { lines, tone },
    actions,
  };
}
